import { clsx } from 'clsx'
import { addLockListener } from 'hook/useBodyScrollLock'
import { useRefState } from 'hook/useRefState'
import { observer } from 'mobx-react-lite'
import { OverlayScrollbars } from 'overlayscrollbars'
import { Footer } from 'page/Footer/Footer'
import { StandHeader } from 'page/StandHeader/StandHeader'
import { ReactNode, useEffect, useState } from 'react'
import { opt } from 'store/opt'
import { stand } from 'store/stand'
import { store } from 'store/store'
import { styles } from 'util/style'
import css from './StandPage.module.scss'

type Props = {
  children: ReactNode
  center?: boolean
  tall?: boolean
  clear?: boolean
  long?: boolean
  full?: boolean
}

function getScreenKey(): string {
  const { screen, profile } = store
  const id = profile?.identity_id?.substring(0, 8)
  if (id && screen === 'profile') return screen + '.' + id
  return screen
}

export const StandPage = observer((props: Props) => {
  const screen = getScreenKey()
  const { children, center, tall, clear, long, full } = props
  const className = clsx(css.page, center && css.center, tall && css.tall, clear && css.clear, long && css.long, full && css.full)
  const style = styles({ '--page-top': stand.top + 'px' })

  const [element, setElement] = useRefState<HTMLDivElement>()
  const [os, setOs] = useRefState<OverlayScrollbars>()
  const [lock, setLock] = useState(false)

  useEffect(() => {
    return addLockListener(event => setLock(event.lock))
  }, [])

  useEffect(() => {
    if (!element) return

    const os = OverlayScrollbars(
      { target: element },
      {
        showNativeOverlaidScrollbars: true,
        overflow: { y: 'hidden' },
        scrollbars: { autoHide: 'leave', autoHideDelay: 500 },
      },
    )
    setOs(os)

    return () => os.destroy()
  }, [element])

  useEffect(() => {
    if (os) os.options({
      overflow: { y: lock ? 'hidden' : 'scroll' },
    })
  }, [os, lock])

  useEffect(() => {
    if (!os) return
    const { viewport } = os.elements()
    const listener = () => {
      stand.scroll = viewport.scrollTop
    }
    viewport.addEventListener('scroll', listener)
    return () => viewport.removeEventListener('scroll', listener)
  }, [os])

  useEffect(() => {
    if (!os) return
    const { viewport } = os.elements()
    viewport.scrollTop = 0
    stand.scroll = 0
  }, [os, screen])

  return <div className={className} style={style}>
    <StandHeader />

    <div ref={setElement} className={css.scroll}>
      <div className={css.container}>
        {children}
      </div>
    </div>

    {opt.footer && (
      <div className={css.footer}>
        <Footer />
      </div>
    )}
  </div>
})
